// src/components/CheckoutSignoffModal.jsx
//
// Generic modal for signing off, undoing, or marking N/A on a single section
// of a student's checkout record.
//
// Modes:
//   - 'sign'  : record a signoff (notes optional)
//   - 'undo'  : revert a signoff (reason optional)
//   - 'na'    : superadmin-only, mark a section N/A even if not from form template
//
// Special handling for the OSL section: when other sections are still pending
// AND the current user does NOT have the override permission, the confirm
// button is disabled with an explanatory tooltip. When other sections are
// pending and the user DOES have override, a yellow warning is shown but
// they can proceed.
//
// For the dorm section, callers should use CheckoutDormSignoffModal instead
// — this component will redirect via a `requiresDormModal` prop signal if
// passed a dorm section.

import React, { useState } from 'react';
import { getFunctions, httpsCallable } from 'firebase/functions';
import {
  getSectionConfig,
  countPendingNonOslSections,
  canOverrideFinalSignoff,
  KA_COLORS,
} from '../utils/checkoutHelpers';

const styles = {
  overlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: 'rgba(0, 0, 0, 0.55)',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    zIndex: 1000, padding: 16,
  },
  modal: {
    background: '#fff', borderRadius: 8,
    width: '100%', maxWidth: 520, maxHeight: '90vh', overflow: 'auto',
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.25)',
    fontFamily: '"Helvetica Neue", Helvetica, Arial, sans-serif',
  },
  header: (mode) => ({
    background: mode === 'undo' ? '#777' : KA_COLORS.navy,
    color: '#fff',
    padding: '16px 20px', borderRadius: '8px 8px 0 0',
  }),
  headerTitle: { margin: 0, fontSize: 18, fontWeight: 700 },
  headerSubtitle: { margin: '4px 0 0', fontSize: 13, opacity: 0.85 },
  body: { padding: 20 },
  studentBlock: {
    background: KA_COLORS.lightGray, borderRadius: 6,
    padding: 12, marginBottom: 16,
  },
  studentName: { fontSize: 14, fontWeight: 600, color: KA_COLORS.navy, margin: 0 },
  studentMeta: { fontSize: 12, color: '#666', margin: '4px 0 0' },
  sectionLabel: {
    fontSize: 13, fontWeight: 700, color: KA_COLORS.navy,
    textTransform: 'uppercase', letterSpacing: 0.5,
    marginBottom: 4,
  },
  sectionTitle: {
    fontSize: 16, fontWeight: 600, color: '#222',
    margin: '0 0 4px',
  },
  sectionDesc: {
    fontSize: 13, color: '#555', margin: 0, lineHeight: 1.4,
  },
  divider: {
    border: 'none',
    borderTop: `1px solid ${KA_COLORS.borderGray}`,
    margin: '16px 0',
  },
  fieldLabel: {
    display: 'block', fontSize: 13, fontWeight: 600,
    color: '#333', marginBottom: 6,
  },
  textarea: {
    width: '100%', padding: '8px 10px',
    border: `1px solid ${KA_COLORS.borderGray}`, borderRadius: 4,
    fontSize: 13, minHeight: 80, fontFamily: 'inherit',
    resize: 'vertical', boxSizing: 'border-box',
  },
  warning: {
    marginTop: 12, padding: 12,
    background: '#FFF3CD', border: '1px solid #FFEEBA',
    borderRadius: 4, color: '#856404', fontSize: 13, lineHeight: 1.5,
  },
  blocker: {
    marginTop: 12, padding: 12,
    background: '#FFEBEE', border: '1px solid #FFCDD2',
    borderRadius: 4, color: '#C62828', fontSize: 13, lineHeight: 1.5,
  },
  errorBox: {
    marginTop: 12, padding: 10,
    background: '#FFEBEE', border: '1px solid #FFCDD2',
    borderRadius: 4, color: '#C62828', fontSize: 13,
  },
  prevSignoff: {
    padding: 10, marginBottom: 12,
    background: '#E8F5E9', border: '1px solid #C8E6C9',
    borderRadius: 4, fontSize: 13, color: '#2E7D32',
  },
  footer: {
    padding: '14px 20px', borderTop: `1px solid ${KA_COLORS.borderGray}`,
    display: 'flex', justifyContent: 'flex-end', gap: 10,
  },
  cancelBtn: {
    padding: '8px 16px', border: `1px solid ${KA_COLORS.borderGray}`,
    background: '#fff', borderRadius: 4, cursor: 'pointer',
    fontSize: 14, color: '#333',
  },
  confirmBtn: (disabled, mode) => ({
    padding: '8px 16px', border: 'none',
    background: disabled
      ? '#ccc'
      : mode === 'undo'
      ? '#777'
      : KA_COLORS.red,
    color: '#fff', borderRadius: 4,
    cursor: disabled ? 'not-allowed' : 'pointer',
    fontSize: 14, fontWeight: 600,
  }),
};

// -----------------------------------------------------------------------------

const CheckoutSignoffModal = ({
  record,
  sectionId,
  teacherSisId = null, // optional: identifies a specific teacher slot
                        // when the section has a teachers[] array
  user, // current auth user (incl. isSuperAdmin, checkoutOverrides)
  mode = 'sign', // 'sign' | 'undo' | 'na'
  onClose,
  onSuccess,
}) => {
  const [notes, setNotes] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const section = getSectionConfig(sectionId);
  if (!record || !section) return null;

  const sectionData = record.sections?.[sectionId] || {};
  const isOsl = section.isFinalSignoff === true;
  const pendingCount = countPendingNonOslSections(record);
  const requiresOverride = isOsl && mode === 'sign' && pendingCount > 0;
  const userCanOverride = canOverrideFinalSignoff(user, sectionId);
  const blockedByOverride = requiresOverride && !userCanOverride;

  // Find the specific teacher slot we're acting on (if any) to surface
  // their name in the modal copy.
  const teacherSlot =
    teacherSisId && Array.isArray(sectionData.teachers)
      ? sectionData.teachers.find(
          (t) => t.sisId && String(t.sisId) === String(teacherSisId),
        ) || null
      : null;

  const handleConfirm = async () => {
    if (blockedByOverride) return;
    setSubmitting(true);
    setError(null);
    try {
      const functions = getFunctions();
      const signoffCheckoutSection = httpsCallable(
        functions,
        'signoffCheckoutSection',
      );
      await signoffCheckoutSection({
        recordId: record.id || `${record.schoolYear}_${record.studentId}`,
        sectionId,
        action: mode, // 'sign' | 'undo' | 'na'
        notes: mode === 'sign' ? notes.trim() : '',
        reason: mode === 'undo' ? reason.trim() : '',
        teacherSisId: teacherSisId || undefined,
      });
      if (onSuccess) onSuccess();
    } catch (err) {
      console.error('signoffCheckoutSection failed:', err);
      setError(err?.message || 'The signoff failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleOverlayClick = (e) => {
    if (e.target === e.currentTarget && !submitting) onClose();
  };

  const titleByMode = {
    sign: 'Sign Off Section',
    undo: 'Undo Signoff',
    na: 'Mark Section N/A',
  };
  const subtitleByMode = {
    sign: `Confirm completion of "${section.label}" for this student.`,
    undo: 'Reverting a prior signoff. Anyone authorized for this section can do this; the action is logged.',
    na: 'Mark this section as not applicable to this student. Superadmin only.',
  };
  const verbByMode = { sign: 'Sign off', undo: 'Confirm undo', na: 'Mark N/A' };

  return (
    <div style={styles.overlay} onClick={handleOverlayClick}>
      <div style={styles.modal} role="dialog" aria-modal="true">
        <div style={styles.header(mode)}>
          <h2 style={styles.headerTitle}>{titleByMode[mode]}</h2>
          <p style={styles.headerSubtitle}>{subtitleByMode[mode]}</p>
        </div>

        <div style={styles.body}>
          <div style={styles.studentBlock}>
            <p style={styles.studentName}>{record.studentName || 'Unknown'}</p>
            <p style={styles.studentMeta}>
              {[record.grade, record.cohort, record.studentId && `SIS ${record.studentId}`]
                .filter(Boolean)
                .join('  •  ')}
            </p>
          </div>

          <div style={styles.sectionLabel}>Section</div>
          <h3 style={styles.sectionTitle}>{section.label}</h3>
          {section.description && (
            <p style={styles.sectionDesc}>{section.description}</p>
          )}

          {/* When acting on a specific teacher slot in a multi-teacher
              section, surface which teacher we're signing for. */}
          {teacherSlot && (
            <p style={styles.sectionDesc}>
              <strong>Teacher:</strong> {teacherSlot.name || '(unnamed)'}
              {teacherSlot.sisId && (
                <span style={{ color: '#888' }}> · {teacherSlot.sisId}</span>
              )}
            </p>
          )}

          <hr style={styles.divider} />

          {/* Show prior signoff info when undoing */}
          {mode === 'undo' && teacherSlot && teacherSlot.signedByName && (
            <div style={styles.prevSignoff}>
              This teacher slot was signed off by{' '}
              <strong>{teacherSlot.signedByName}</strong>.
            </div>
          )}
          {mode === 'undo' && !teacherSlot && sectionData.signedByName && (
            <div style={styles.prevSignoff}>
              Currently signed off by{' '}
              <strong>{sectionData.signedByName}</strong>.
              {sectionData.notes && (
                <>
                  <br />
                  <em>Notes:</em> {sectionData.notes}
                </>
              )}
            </div>
          )}

          {/* OSL early-signoff warning / blocker */}
          {requiresOverride && userCanOverride && (
            <div style={styles.warning}>
              <strong>Heads up:</strong> {pendingCount} other section
              {pendingCount === 1 ? '' : 's'} still pending. Because you are an
              authorized override user, you can proceed — but please confirm the
              student has actually fulfilled all obligations.
            </div>
          )}
          {blockedByOverride && (
            <div style={styles.blocker}>
              <strong>Cannot sign off yet.</strong> {pendingCount} section
              {pendingCount === 1 ? '' : 's'} still pending. Only designated
              override users can authorize an early OSL signoff. Ask a
              superadmin or wait for the remaining sections to be completed.
            </div>
          )}

          {/* Notes (sign) / Reason (undo) */}
          {mode === 'sign' && (
            <>
              <label style={styles.fieldLabel} htmlFor="signoff-notes">
                Notes (optional)
              </label>
              <textarea
                id="signoff-notes"
                style={styles.textarea}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="e.g., All 3 textbooks returned in good condition."
                disabled={submitting}
              />
            </>
          )}
          {mode === 'undo' && (
            <>
              <label style={styles.fieldLabel} htmlFor="undo-reason">
                Reason for undo (optional)
              </label>
              <textarea
                id="undo-reason"
                style={styles.textarea}
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g., Signed off by mistake — student still owes 1 book."
                disabled={submitting}
              />
            </>
          )}

          {error && <div style={styles.errorBox}>{error}</div>}
        </div>

        <div style={styles.footer}>
          <button
            type="button"
            style={styles.cancelBtn}
            onClick={onClose}
            disabled={submitting}
          >
            Cancel
          </button>
          <button
            type="button"
            style={styles.confirmBtn(submitting || blockedByOverride, mode)}
            onClick={handleConfirm}
            disabled={submitting || blockedByOverride}
            title={
              blockedByOverride
                ? 'Only override users can sign OSL early.'
                : undefined
            }
          >
            {submitting ? 'Working…' : verbByMode[mode]}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSignoffModal;